import React, { useEffect, useReducer } from 'react'
import { Button, Modal } from 'react-bootstrap'

import { ToastType, useToast } from '../../../hooks/useToast'
import { useChangeWarehouseLocationForPartsMutation } from '../../../modules/store/datahubApi/datahubApi'
import { WarehouseLocationData } from '../../../modules/store/datahubApi/datahubTypes'
import { partReducer } from '../../../pages/NewPart/partReducer'
import { Part, partFields, partInput, PartListSearchParams } from '../../../types/part/partTypes'
import Loader from '../../_atoms/Loader/Loader'
import OverlayWithText from '../../_atoms/OverlayWithText/OverlayWithText'
import PartCreateUpdateTable from '../PartCreateUpdateTable/PartCreateUpdateTable'

import { initialPartState } from './initialPartState'
import styles from './styles.module.scss'

interface Props {
    isOpen: boolean
    closeModal: () => void
    parts: Part[]
    confirmChanges: () => void
    partListSearchParams?: PartListSearchParams
}

const fieldToLocationKey: Partial<Record<partFields, keyof WarehouseLocationData>> = {
    [partFields.warehouseRow]: 'row',
    [partFields.warehouseShelf]: 'shelf',
    [partFields.warehousePlace]: 'place',
    [partFields.warehouseSection]: 'section',
}

export const ModalChangeWarehouseLocation = ({
    isOpen,
    closeModal,
    parts,
    confirmChanges,
    partListSearchParams,
}: Props) => {
    const [state, dispatch] = useReducer(partReducer, initialPartState)
    const [fetchChangeWarehouseLocation, { isLoading, error, data }] = useChangeWarehouseLocationForPartsMutation()

    const location = state.fields.reduce((acc: WarehouseLocationData, item: partInput) => {
        const key = fieldToLocationKey[item.field as partFields]
        const value = String(item.value || '').trim()
        if (key && value) {
            acc[key] = value
        }
        return acc
    }, {})

    const onCloseModal = () => {
        closeModal()
    }

    const confirm = () => {
        if (!Object.keys(location).length) return
        fetchChangeWarehouseLocation({
            partsId: parts.map(part => part.id),
            location,
            params: partListSearchParams,
        })
    }

    useEffect(() => {
        if (data) {
            confirmChanges()
            closeModal()
        }
    }, [closeModal, confirmChanges, data])

    useToast({ isTriggered: !!error, type: ToastType.ERROR, message: 'Ошибка изменения ряда, полки, места и секции' })
    useToast({ isTriggered: !!data, type: ToastType.SUCCESS, message: 'Ряд, полка, место и секция обновлены' })

    return (
        <Modal show={isOpen} onHide={onCloseModal} centered dialogClassName={styles.modal}>
            {isLoading && (
                <OverlayWithText backgroundBootstrapColor={'bg-secondary'}>
                    <Loader diameterInPx={100} thicknessInPx={10} />
                </OverlayWithText>
            )}
            <Modal.Header closeButton>
                <Modal.Title>
                    <b>Ряд/Полка/Место/Секция</b>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <PartCreateUpdateTable dispatch={dispatch} fields={state.fields} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={confirm} disabled={!Object.keys(location).length}>
                    Сохранить
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
